import { Component, HostListener, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import {
    ConfiguratorDatastoreService,
    ConfiguratorI18nCacheService,
    ConfiguratorTemplateHtmlService,
    I18nStringItem,
    TemplateHtmlDTO
} from '@tj/tj-dmp-lib-api';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { TranslationManagerComponent } from '../../components/translation-manager/translation-manager.component';

const LIST_URL = '/h/marketing/template';
/** Clé de `htmlByAffi` réservée au contenu par défaut, servi aux affiliés sans contenu propre. */
const DEFAULT_VARIANT = 'DEFAULT';

/** Code d'un nouveau bloc, calculé d'après son nom. Il ne change plus après la création. */
export function blocCode(name: string): string {
    return 'TPL_' + (name ?? '').trim().toUpperCase().replace(/\s+/g, '_');
}

interface VariantOption {
    code: string;
    label: string;
}

/**
 * Création et modification d'un bloc de template HTML (cf. docs/ui-design-guidelines.md §12, §17) :
 * informations générales, contenu (code et aperçu côte à côte, un contenu par défaut et un contenu propre
 * par affilié), puis les traductions que le code insère sous la forme {{clé}}.
 */
@Component({
    templateUrl: './bloc-edit-html.component.html',
    styleUrls: ['./bloc-edit-html.component.scss'],
    standalone: false
})
export class BlocEditHtmlComponent implements OnInit {

    readonly defaultVariant = DEFAULT_VARIANT;
    /** Exemple de clé dans l'aide des traductions : en paramètre, ngx-translate lirait les accolades. */
    readonly keyPlaceholder = '{{cle}}';

    breadcrumb: { label: string; routerLink?: string }[] = [];
    form: UntypedFormGroup;
    actual: TemplateHtmlDTO;
    loaded = false;
    saving = false;
    submitted = false;

    tenant: string;
    tenantName: string;

    /** Code HTML par variante : `DEFAULT`, puis un code d'affilié par contenu propre. */
    codeAffiliations: Record<string, string> = {};
    /** Affiliés pour lesquels on a choisi explicitement de reprendre (ou non) le contenu par défaut. */
    statusAffiliations: Record<string, boolean> = {};
    variant = DEFAULT_VARIANT;
    variants: VariantOption[] = [];
    translations: I18nStringItem[] = [];
    editor: any;

    constructor(
        private fb: UntypedFormBuilder,
        protected templateHtmlService: ConfiguratorTemplateHtmlService,
        private datastoreService: ConfiguratorDatastoreService,
        private route: ActivatedRoute,
        protected i18nCacheService: ConfiguratorI18nCacheService,
        private messageService: MessageService,
        private router: Router,
        private location: Location,
        private readonly translate: TranslateService,
    ) {
    }

    get f(): { [key: string]: AbstractControl } {
        return this.form.controls;
    }

    /** Figé à la création : les landing pages désignent le bloc par ce code, en texte dans leur contenu. */
    get code(): string {
        return this.actual?.code ?? blocCode(this.form.value.name);
    }

    get title(): string {
        return this.actual
            ? this.translate.instant('htmlBlockEdit.titleEdit', {name: this.actual.name})
            : this.translate.instant('htmlBlockEdit.titleCreate');
    }

    get isDefaultVariant(): boolean {
        return this.variant === DEFAULT_VARIANT;
    }

    get variantCode(): string {
        return this.codeAffiliations[this.variant] ?? '';
    }

    set variantCode(code: string) {
        this.codeAffiliations[this.variant] = code;
    }

    /** L'affilié affiché reprend le contenu par défaut : choix explicite, ou à défaut absence de contenu propre. */
    get useDefault(): boolean {
        return this.usesDefault(this.variant);
    }

    set useDefault(useDefault: boolean) {
        this.statusAffiliations[this.variant] = useDefault;
        // Un contenu propre part d'une copie du contenu par défaut, pas d'une page vide.
        if (!useDefault && !this.codeAffiliations[this.variant]) {
            this.codeAffiliations[this.variant] = this.codeAffiliations[DEFAULT_VARIANT] ?? '';
        }
    }

    ngOnInit() {
        this.tenant = this.route.snapshot.paramMap.get('tenant');

        this.form = this.fb.group({
            partner: ['', [Validators.required]],
            name: ['', [Validators.required, Validators.pattern(/^[\w\s]*$/)]],
            description: [''],
            enabled: [true],
        });
        this.updateBreadcrumb();

        this.datastoreService.getAllDatastoresUsingGET().subscribe(datastores => {
            this.tenantName = datastores.find(d => d.code === this.tenant)?.name;
        });
        this.i18nCacheService.searchClientUsingGET().subscribe(clients => {
            this.variants = [
                {code: DEFAULT_VARIANT, label: this.translate.instant('htmlBlockEdit.content.variantDefault')},
                ...clients.map(c => ({code: c.key, label: c.value}))
            ];
        });
        this.load();
    }

    /** Ctrl+S (Cmd+S) enregistre sans quitter, comme dans un éditeur de code. */
    @HostListener('document:keydown', ['$event'])
    onKeydown(event: KeyboardEvent): void {
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
            event.preventDefault();
            this.save(false);
        }
    }

    showError(control: string): boolean {
        const c = this.f[control];
        return c.invalid && (c.dirty || c.touched || this.submitted);
    }

    usesDefault(variant: string): boolean {
        if (variant === DEFAULT_VARIANT) {
            return false;
        }
        return this.statusAffiliations[variant] ?? !this.codeAffiliations[variant];
    }

    save(close: boolean): void {
        this.submitted = true;
        this.form.markAllAsTouched();
        if (this.form.invalid || this.saving) {
            return;
        }
        this.saving = true;
        const value = this.form.value;
        const html: Record<string, string> = {...this.codeAffiliations};
        delete html[DEFAULT_VARIANT];
        // Un affilié qui reprend le contenu par défaut (case cochée, ou contenu vidé) est enregistré à `null`,
        // comme l'écran le montre ; un affilié jamais ouvert reste absent.
        Object.keys({...html, ...this.statusAffiliations}).forEach(code => {
            if (this.usesDefault(code)) {
                html[code] = null;
            }
        });
        // On part de l'élément chargé : les champs que l'écran ne montre pas (tags) ne sont pas effacés.
        const bloc: TemplateHtmlDTO = {
            ...this.actual,
            code: this.code,
            defaultHtml: this.codeAffiliations[DEFAULT_VARIANT],
            htmlByAffi: html,
            description: value.description,
            dictionary: this.translations,
            enable: value.enabled,
            name: value.name.trim(),
            partner: value.partner,
            tenant: this.tenant,
            templateEngine: 'jtwig'
        };
        const created = !this.actual;
        const request = created
            ? this.templateHtmlService.createTemplateHtmlUsingPOST(this.tenant, bloc)
            : this.templateHtmlService.updateTemplateHtmlUsingPUT(this.tenant, bloc);

        request.subscribe({
            next: (saved) => {
                this.saving = false;
                if (close) {
                    const navigationExtras: NavigationExtras = {state: {message: created ? 'success_created' : 'success_updated'}};
                    this.router.navigate([LIST_URL], navigationExtras);
                    return;
                }
                this.actual = {...bloc, ...saved, id: saved?.id ?? this.actual?.id};
                if (created) {
                    this.location.replaceState(`${LIST_URL}/${this.tenant}/${this.actual.id}/edit`);
                }
                this.form.markAsPristine();
                this.updateBreadcrumb();
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('htmlBlockEdit.toasts.saved'),
                    detail: this.actual.name
                });
            },
            error: (e) => {
                this.saving = false;
                // `ErrorsInterceptor` a déjà affiché les erreurs qui portent un titre serveur (§12).
                if (e instanceof HttpErrorResponse) {
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('htmlBlockEdit.toasts.saveError'),
                        detail: e.message
                    });
                }
            }
        });
    }

    appendKeyInCode(key: string) {
        if (!this.editor || this.useDefault) {
            return;
        }
        const pos = this.editor.getPosition() as { lineNumber: number, column: number };
        const array = this.variantCode.split('\n');
        array[pos.lineNumber - 1] = array[pos.lineNumber - 1].slice(0, pos.column - 1) + `{{${key}}}` + array[pos.lineNumber - 1].slice(pos.column - 1);
        this.variantCode = array.join('\n');
    }

    addTranslation(manager: TranslationManagerComponent): void {
        manager.create();
    }

    /** En création, `dmp-select-partner` annonce le partenaire mémorisé par `change` seulement, pas au formulaire. */
    partnerChange(partner: string) {
        this.form.patchValue({partner});
    }

    private load(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (!id) {
            this.loaded = true;
            return;
        }
        this.templateHtmlService.getTemplateHtmlUsingGET(this.tenant, id).subscribe({
            next: (res) => {
                this.actual = res;
                this.codeAffiliations = {...(res.htmlByAffi ?? {}), [DEFAULT_VARIANT]: res.defaultHtml};
                Object.entries(res.htmlByAffi ?? {}).forEach(([code, value]) => {
                    this.statusAffiliations[code] = value === null;
                });
                this.translations = res.dictionary ?? [];
                this.form.reset({
                    partner: res.partner,
                    name: res.name,
                    description: res.description,
                    enabled: res.enable,
                });
                this.updateBreadcrumb();
                this.loaded = true;
            },
            error: (e) => {
                if (e instanceof HttpErrorResponse) {
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('htmlBlockEdit.toasts.loadError'),
                        detail: e.message
                    });
                }
            }
        });
    }

    private updateBreadcrumb(): void {
        this.breadcrumb = [
            {label: this.translate.instant('menu.marketing.label')},
            {label: this.translate.instant('menu.workflow.html-blocks'), routerLink: LIST_URL},
            {label: this.actual?.name ?? this.translate.instant('htmlBlockEdit.titleCreate')}
        ];
    }
}
